<template>
    <div>
        <Modal size="lg" @close="$emit('close')">
            <template #header>
                <div class="text-gray-600">
                    {{
                        isSuccess
                            ? $t("vue.plan_updated_successfully")
                            : $t("vue.switch_plan")
                    }}
                </div>
            </template>

            <template #body>
                <div class="bg-white p-6">
                    <!-- Success State -->
                    <div v-if="isSuccess" class="space-y-6">
                        <div
                            class="rounded-md border border-green-200 bg-green-50 p-6 text-center"
                        >
                            <div
                                class="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-green-100"
                            >
                                <svg
                                    class="h-6 w-6 text-green-600"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                >
                                    <path
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                        stroke-width="2"
                                        d="M5 13l4 4L19 7"
                                    />
                                </svg>
                            </div>
                            <h3 class="mt-4 text-lg font-medium text-green-800">
                                {{
                                    $t(
                                        "vue.plan_updated_successfully_exclamation",
                                    )
                                }}
                            </h3>
                            <p class="mt-2 text-sm text-green-700">
                                {{ $t("vue.plan_changed_from_to") }}
                                <span class="font-medium">{{
                                    currentPlanName
                                }}</span>
                                {{ $t("vue.to") }}
                                <span class="font-medium">{{
                                    newPlanName
                                }}</span
                                >.
                            </p>
                        </div>

                        <div
                            class="rounded-md border border-blue-200 bg-blue-50 p-4"
                        >
                            <h4 class="mb-2 text-lg font-medium text-gray-800">
                                {{ $t("vue.what_happens_next") }}
                            </h4>
                            <ul class="space-y-2 text-sm text-gray-700">
                                <li class="flex items-start">
                                    <span class="mr-2 text-blue-600">•</span>
                                    {{
                                        $t("vue.current_billing_cycle_continue")
                                    }}
                                    {{ formatNextBillingDate }}
                                </li>
                                <li class="flex items-start">
                                    <span class="mr-2 text-blue-600">•</span>
                                    {{ $t("vue.new_plan_take_effect") }}
                                </li>
                                <li class="flex items-start">
                                    <span class="mr-2 text-blue-600">•</span>
                                    {{ $t("vue.switch_back_previous_plan") }}
                                </li>
                                <li class="flex items-start">
                                    <span class="mr-2 text-blue-600">•</span>
                                    {{ $t("vue.no_immediate_charges") }}
                                </li>
                            </ul>
                        </div>
                    </div>

                    <!-- Confirmation State -->
                    <div v-else class="space-y-6">
                        <!-- Current Plan Info -->
                        <div
                            class="rounded-md border border-gray-200 bg-gray-50 p-4"
                        >
                            <h4 class="mb-2 text-lg font-medium text-gray-800">
                                {{ $t("vue.current_plan") }}
                            </h4>
                            <div class="grid grid-cols-2 gap-4 text-sm">
                                <div>
                                    <span class="font-medium text-gray-600"
                                        >{{ $t("vue.plan") }}:</span
                                    >
                                    <span class="ml-2 text-gray-800">{{
                                        currentPlanName
                                    }}</span>
                                </div>
                                <div>
                                    <span class="font-medium text-gray-600"
                                        >{{
                                            $t("vue.current_frequency")
                                        }}:</span
                                    >
                                    <span class="ml-2 text-gray-800">{{
                                        currentFrequency === "year"
                                            ? $t("vue.yearly")
                                            : $t("vue.monthly")
                                    }}</span>
                                </div>
                                <div>
                                    <span class="font-medium text-gray-600"
                                        >{{ $t("vue.current_price") }}:</span
                                    >
                                    <span class="ml-2 text-gray-800"
                                        >${{ currentPrice }}</span
                                    >
                                </div>
                                <div>
                                    <span class="font-medium text-gray-600"
                                        >{{ $t("vue.next_billing") }}:</span
                                    >
                                    <span class="ml-2 text-gray-800">{{
                                        formatNextBillingDate
                                    }}</span>
                                </div>
                            </div>
                        </div>

                        <!-- New Plan Info -->
                        <div
                            class="rounded-md border border-blue-200 bg-blue-50 p-4"
                        >
                            <h4 class="mb-2 text-lg font-medium text-gray-800">
                                {{ $t("vue.new_plan") }}
                            </h4>
                            <div
                                v-if="isLoadingPreview"
                                class="flex items-center justify-center py-4"
                            >
                                <div class="text-gray-600">
                                    {{ $t("vue.loading_preview") }}
                                </div>
                            </div>
                            <div
                                v-else-if="previewData"
                                class="grid grid-cols-2 gap-4 text-sm"
                            >
                                <div>
                                    <span class="font-medium text-gray-600"
                                        >{{ $t("vue.plan") }}:</span
                                    >
                                    <span class="ml-2 text-gray-800">{{
                                        newPlanName
                                    }}</span>
                                </div>
                                <div>
                                    <span class="font-medium text-gray-600"
                                        >{{ $t("vue.new_frequency") }}:</span
                                    >
                                    <span class="ml-2 text-gray-800">{{
                                        newFrequency === "year"
                                            ? $t("vue.yearly")
                                            : $t("vue.monthly")
                                    }}</span>
                                </div>
                                <div>
                                    <span class="font-medium text-gray-600"
                                        >{{ $t("vue.new_price") }}:</span
                                    >
                                    <span class="ml-2 text-gray-800"
                                        >${{ newTotal }}</span
                                    >
                                </div>
                                <div>
                                    <span class="font-medium text-gray-600"
                                        >{{ $t("vue.effective_date") }}:</span
                                    >
                                    <span class="ml-2 text-gray-800">{{
                                        formatEffectiveDate
                                    }}</span>
                                </div>
                                <div class="col-span-2">
                                    <span class="font-medium text-gray-600"
                                        >{{ $t("vue.immediate_charge") }}:</span
                                    >
                                    <span
                                        class="ml-2"
                                        :class="immediateTransactionClass"
                                    >
                                        {{ immediateTransactionText }}
                                    </span>
                                    <span
                                        v-if="isChargeCoveredByCredits"
                                        class="ml-2 font-medium text-green-600"
                                    >
                                        ✓
                                        {{ $t("vue.fully_covered_by_credits") }}
                                    </span>
                                    <span
                                        v-else-if="
                                            isChargePartiallyCoveredByCredits
                                        "
                                        class="ml-2 font-medium text-green-600"
                                    >
                                        ✓
                                        {{
                                            $t(
                                                "vue.partially_covered_by_credits",
                                            )
                                        }}
                                    </span>
                                </div>
                                <div v-if="creditsUsed" class="col-span-2">
                                    <span class="font-medium text-gray-600"
                                        >{{ $t("vue.credits_used") }}:</span
                                    >
                                    <span class="ml-2 text-green-600">
                                        ${{ creditsUsed }}
                                    </span>
                                </div>
                            </div>
                            <div v-else class="text-sm text-red-600">
                                {{ $t("vue.failed_to_load_preview_data") }}
                            </div>
                        </div>

                        <!-- Important Notes -->
                        <div
                            class="rounded-md border border-yellow-200 bg-yellow-50 p-4"
                        >
                            <h4 class="mb-2 text-lg font-medium text-gray-800">
                                {{ $t("vue.important_information") }}
                            </h4>
                            <ul class="space-y-1 text-sm text-gray-700">
                                <li
                                    v-if="
                                        immediateTransactionAmount &&
                                        immediateTransactionAmount !== '0'
                                    "
                                >
                                    <span v-if="isChargeCoveredByCredits">
                                        •
                                        {{
                                            $t(
                                                "vue.immediate_charge_applied_covered_by_credits",
                                            )
                                        }}
                                    </span>
                                    <span
                                        v-else-if="
                                            isChargePartiallyCoveredByCredits
                                        "
                                    >
                                        •
                                        {{
                                            $t(
                                                "vue.immediate_charge_applied_partially_covered_by_credits",
                                            )
                                        }}
                                    </span>
                                    <span v-else>
                                        •
                                        {{
                                            $t(
                                                "vue.immediate_charge_applied_to_account",
                                            )
                                        }}
                                    </span>
                                </li>
                                <li v-else>
                                    •
                                    {{
                                        $t(
                                            "vue.no_immediate_charges_for_this_change",
                                        )
                                    }}
                                </li>
                                <li>
                                    •
                                    {{
                                        $t(
                                            "vue.current_billing_cycle_continue_until_next_billing_date",
                                        )
                                    }}
                                </li>
                                <li>
                                    •
                                    {{
                                        $t(
                                            "vue.new_plan_take_effect_on_next_billing_cycle",
                                        )
                                    }}
                                </li>
                                <li>
                                    •
                                    {{
                                        $t(
                                            "vue.switch_back_previous_plan_at_any_time",
                                        )
                                    }}
                                </li>
                                <li
                                    v-if="
                                        creditsUsed &&
                                        !isChargeCoveredByCredits &&
                                        !isChargePartiallyCoveredByCredits
                                    "
                                >
                                    • {{ $t("vue.credits_applied_to_account") }}
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
            </template>

            <template #footer>
                <div
                    class="flex justify-end space-x-3 border-t border-gray-200 px-6 py-4"
                >
                    <AtomicButton
                        v-if="!isSuccess"
                        color="white"
                        @click="$emit('close')"
                    >
                        {{ $t("vue.cancel") }}
                    </AtomicButton>
                    <AtomicButton
                        v-if="!isSuccess"
                        color="yellow"
                        :disabled="isLoading"
                        @click="confirmPlanSwitch"
                    >
                        <span v-if="isLoading">{{ $t("vue.processing") }}</span>
                        <span v-else>{{ $t("vue.confirm_switch") }}</span>
                    </AtomicButton>
                    <AtomicButton
                        v-if="isSuccess"
                        color="yellow"
                        @click="$emit('close')"
                    >
                        {{ $t("vue.close") }}
                    </AtomicButton>
                </div>
            </template>
        </Modal>
    </div>
</template>

<script lang="ts" setup>
import { computed } from "vue";
import { format } from "date-fns";
import { router } from "@inertiajs/vue3";
import route from "ziggy-js";

const props = defineProps({
    currentFrequency: {
        type: String as () => "month" | "year",
        required: true,
    },
    currentPrice: {
        type: String,
        required: true,
    },
    currentPlanName: {
        type: String,
        required: true,
    },
    nextBillingDate: {
        type: String,
        required: true,
    },
    newPlanPriceId: {
        type: String,
        required: true,
    },
    newPlanName: {
        type: String,
        required: true,
    },
    newFrequency: {
        type: String as () => "month" | "year",
        required: true,
    },
});

const emit = defineEmits(["close", "success"]);
const isLoading = ref(false);
const isSuccess = ref(false);
const previewData = ref(null as any);
const isLoadingPreview = ref(false);

const newFrequency = computed(() => {
    return props.newFrequency;
});

const formatNextBillingDate = computed(() => {
    if (!props.nextBillingDate) return "N/A";
    return format(new Date(props.nextBillingDate), "MMMM d, yyyy");
});

const formatEffectiveDate = computed(() => {
    if (
        immediateTransactionAmount.value &&
        immediateTransactionAmount.value !== "0"
    ) {
        return "Immediate";
    }
    if (!props.nextBillingDate) return "N/A";
    return format(new Date(props.nextBillingDate), "MMMM d, yyyy");
});

const immediateTransactionAmount = computed(() => {
    return previewData.value?.immediate_transaction_total?.toString() || "0";
});

const immediateTransactionClass = computed(() => {
    if (!immediateTransactionAmount.value) return "text-gray-600";

    const amount = parseFloat(immediateTransactionAmount.value);
    if (amount > 0) {
        return "text-red-600 font-medium";
    } else if (amount < 0) {
        return "text-green-600 font-medium";
    }
    return "text-gray-600";
});

const immediateTransactionText = computed(() => {
    const amount = parseFloat(immediateTransactionAmount.value);
    if (amount < 0) {
        return `-$${Math.abs(amount).toFixed(2)} (credit)`;
    }
    return `$${amount.toFixed(2)}`;
});

const creditsUsed = computed(() => {
    if (!previewData.value?.credits_used) return null;
    return Math.abs(parseFloat(previewData.value.credits_used)).toFixed(2);
});

const isChargeCoveredByCredits = computed(() => {
    if (!immediateTransactionAmount.value || !creditsUsed.value) return false;

    console.log(immediateTransactionAmount.value, creditsUsed.value);
    const chargeAmount = parseFloat(immediateTransactionAmount.value);
    const creditAmount = parseFloat(creditsUsed.value);
    return chargeAmount > 0 && creditAmount >= chargeAmount;
});

const isChargePartiallyCoveredByCredits = computed(() => {
    if (!immediateTransactionAmount.value || !creditsUsed.value) return false;
    const chargeAmount = parseFloat(immediateTransactionAmount.value);
    const creditAmount = parseFloat(creditsUsed.value);
    return chargeAmount > 0 && creditAmount > 0 && creditAmount < chargeAmount;
});

const newTotal = computed(() => {
    return previewData.value?.new_total?.toString() || "0";
});

const fetchPreviewData = async () => {
    isLoadingPreview.value = true;

    try {
        const response = await axios.get(
            route("billing.subscriptions.plan.preview"),
            {
                params: {
                    price_id: props.newPlanPriceId,
                },
            },
        );
        previewData.value = response.data;
    } catch (error) {
        console.error("Failed to fetch preview data:", error);
    } finally {
        isLoadingPreview.value = false;
    }
};

const confirmPlanSwitch = async () => {
    isLoading.value = true;

    try {
        await router.post(
            route("billing.subscriptions.switch"),
            {
                price_id: props.newPlanPriceId,
            },
            {
                onSuccess: () => {
                    isSuccess.value = true;
                    isLoading.value = false;
                    // Emit success event to parent
                    emit("success");
                },
                onError: (errors) => {
                    console.error("Plan switch failed:", errors);
                    isLoading.value = false;
                },
                onFinish: () => {
                    isLoading.value = false;
                },
            },
        );
    } catch (error) {
        console.error("Plan switch error:", error);
        isLoading.value = false;
    }
};

// Fetch preview data when modal opens
onMounted(() => {
    fetchPreviewData();
});
</script>

<style scoped>
/* Add any custom styles here if needed */
</style>
